import * as s from '~js/utils/selectors';
import * as a from '~js/utils/attributes';
import * as c from '~js/utils/classes';

export class Popout {
  constructor(popout) {
    this.popout = popout;
    this.popoutList = this.popout.querySelector(s.selectors$N.popoutList);
    this.popoutToggle = this.popout.querySelector(s.selectors$N.popoutToggle);
    this.popoutText = this.popout.querySelector(s.selectors$N.popoutText);
    this.popoutInput = this.popout.querySelector(s.selectors$N.popoutInput);
    this.popoutOptions = this.popout.querySelectorAll(s.selectors$N.popoutOptions);
    this.popoutPrevent = this.popout.getAttribute(a.attributes$w.popoutPrevent) === 'true';
    this.popupToggleFocusoutEvent = (evt) => this.popupToggleFocusout(evt);
    this.popupListFocusoutEvent = (evt) => this.popupListFocusout(evt);
    this.popupToggleClickEvent = (evt) => this.popupToggleClick(evt);
    this.popoutKeyupEvent = (evt) => this.popoutKeyup(evt);
    this.popupOptionsClickEvent = (evt) => this.popupOptionsClick(evt);
    this._connectOptionsDispatchEvent = (evt) => this._connectOptionsDispatch(evt);
    this.bodyClick = this.bodyClick.bind(this);
    this.updatePopout = this.updatePopout.bind(this);

    this._connectOptions();
    this._connectToggle();
    this._onFocusOut();

    if (this.popoutInput && this.popoutInput.hasAttribute(a.attributes$w.popoutQuantity)) {
      document.addEventListener('theme:cart:update', this.updatePopout);
    }
  }

  unload() {
    if (this.popoutOptions.length) {
      this.popoutOptions.forEach((element) => {
        element.removeEventListener('theme:popout:click', this.popupOptionsClickEvent);
        element.removeEventListener('click', this._connectOptionsDispatchEvent);
      });
    }

    this.popoutToggle.removeEventListener('click', this.popupToggleClickEvent);
    this.popoutToggle.removeEventListener('focusout', this.popupToggleFocusoutEvent);
    this.popoutList.removeEventListener('focusout', this.popupListFocusoutEvent);
    this.popout.removeEventListener('keyup', this.popoutKeyupEvent);
    document.removeEventListener('theme:cart:update', this.updatePopout);
    document.body.removeEventListener('click', this.bodyClick);
  }

  popupToggleClick(evt) {
    const ariaExpanded = evt.currentTarget.getAttribute(a.attributes$w.ariaExpanded) === 'true';

    if (this.popoutList.closest(s.selectors$N.productGrid)) {
      const productGridItemImage = this.popoutList
        .closest(s.selectors$N.productGrid)
        .querySelector(s.selectors$N.productGridImage);

      if (productGridItemImage) {
        productGridItemImage.classList.toggle(c.classes$D.visible, !ariaExpanded);
      }
    }

    evt.currentTarget.setAttribute(a.attributes$w.ariaExpanded, !ariaExpanded);
    this.popoutList.classList.toggle(c.classes$D.listVisible);
  }

  popupToggleFocusout(evt) {
    if (!evt.relatedTarget) {
      return;
    }

    const popoutLostFocus = this.popout.contains(evt.relatedTarget);
    const popoutFromQuickView = evt.relatedTarget.hasAttribute(a.attributes$w.quickViewItem);

    if (!popoutLostFocus && !popoutFromQuickView) {
      this._hideList();
    }
  }

  popupListFocusout(evt) {
    const childInFocus = evt.currentTarget.contains(evt.relatedTarget);
    const isVisible = this.popoutList.classList.contains(c.classes$D.listVisible);

    if (isVisible && !childInFocus) {
      this._hideList();
    }
  }

  popupOptionsClick(evt) {
    const link = evt.target.closest(s.selectors$N.popoutOptions);
    if (link.attributes.href.value === '#') {
      evt.preventDefault();

      let attrValue = '';

      if (evt.currentTarget.getAttribute(a.attributes$w.dataValue)) {
        attrValue = evt.currentTarget.getAttribute(a.attributes$w.dataValue);
      }

      this.popoutInput.value = attrValue;

      if (this.popoutPrevent) {
        this.popoutInput.dispatchEvent(new Event('change'));

        if (!evt.detail.preventTrigger && this.popoutInput.hasAttribute(a.attributes$w.popoutQuantity)) {
          this.popoutInput.dispatchEvent(new Event('input'));
        }

        const currentElement = this.popoutList.querySelector(`[class*="${c.classes$D.currentSuffix}"]`);
        let targetClass = c.classes$D.currentSuffix;

        if (currentElement && currentElement.classList.length) {
          for (const currentElementClass of currentElement.classList) {
            if (currentElementClass.includes(c.classes$D.currentSuffix)) {
              targetClass = currentElementClass;
              break;
            }
          }
        }

        const listTargetElement = this.popoutList.querySelector(`.${targetClass}`);

        if (listTargetElement) {
          listTargetElement.classList.remove(`${targetClass}`);
          evt.currentTarget.parentElement.classList.add(`${targetClass}`);
        }

        const targetAttribute = this.popoutList.querySelector(s.selectors$N.ariaCurrent);

        if (targetAttribute) {
          targetAttribute.removeAttribute(a.attributes$w.ariaCurrent);
          evt.currentTarget.setAttribute(a.attributes$w.ariaCurrent, 'true');
        }

        if (attrValue !== '') {
          this.popoutText.textContent = attrValue;
        }

        this.popupToggleFocusout(evt);
        this.popupListFocusout(evt);
      } else {
        this._submitForm(attrValue);
      }
    }
  }

  updatePopout() {
    const targetElement = this.popoutList.querySelector(`[${a.attributes$w.dataValue}="${this.popoutInput.value}"]`);
    if (targetElement) {
      targetElement.dispatchEvent(
        new CustomEvent('theme:popout:click', {
          cancelable: true,
          bubbles: true,
          detail: {
            preventTrigger: true,
          },
        })
      );

      if (!targetElement.parentElement.nextSibling) {
        this.popout.classList.add(c.classes$D.popoutAlternative);
      }
    } else {
      this.popout.classList.add(c.classes$D.popoutAlternative);
    }
  }

  popoutKeyup(event) {
    if (event.code !== theme.keyboardKeys.ESCAPE) {
      return;
    }
    this._hideList();
    this.popoutToggle.focus();
  }

  bodyClick(event) {
    const isOption = this.popout.contains(event.target);
    const isVisible = this.popoutList.classList.contains(c.classes$D.listVisible);

    if (isVisible && !isOption) {
      this._hideList();
    }
  }

  _connectToggle() {
    this.popoutToggle.addEventListener('click', this.popupToggleClickEvent);
  }

  _connectOptions() {
    if (this.popoutOptions.length) {
      this.popoutOptions.forEach((element) => {
        element.addEventListener('theme:popout:click', this.popupOptionsClickEvent);
        element.addEventListener('click', this._connectOptionsDispatchEvent);
      });
    }
  }

  _connectOptionsDispatch(evt) {
    const event = new CustomEvent('theme:popout:click', {
      cancelable: true,
      bubbles: true,
      detail: {
        preventTrigger: false,
      },
    });

    if (!evt.target.dispatchEvent(event)) {
      evt.preventDefault();
    }
  }

  _onFocusOut() {
    this.popoutToggle.addEventListener('focusout', this.popupToggleFocusoutEvent);
    this.popoutList.addEventListener('focusout', this.popupListFocusoutEvent);
    this.popout.addEventListener('keyup', this.popoutKeyupEvent);

    document.body.addEventListener('click', this.bodyClick);
  }

  _submitForm() {
    const form = this.popout.closest(s.selectors$N.form);
    if (form) {
      form.submit();
    }
  }

  _hideList() {
    this.popoutList.classList.remove(c.classes$D.listVisible);
    this.popoutToggle.setAttribute(a.attributes$w.ariaExpanded, false);
  }
}